<script setup>
import {onMounted, ref, computed, watch} from 'vue';
import {useI18n} from 'vue-i18n';

import ChartFilters from '@/components/Filters/ChartFilters.vue';
import {CHART_TYPE} from '@/core/constants/query';

const INITIAL_MODEL = {
    ltd_initial_status: '',
    ltd_final_status: '',
    only_business_days: '',
    only_included_tasks: '',
};

const {t} = useI18n();
const {availableStatuses} = window.settings;

const model = ref({...INITIAL_MODEL});

const isFinalStatusBlocked = computed(() => {
    return !model.value.ltd_initial_status;
});

const initialAvailableFinalStatus = computed(() => {
    if (!model.value.ltd_initial_status) return 0;

    const {position: initialStatusPosition} = availableStatuses.find(({id}) => id === +model.value.ltd_initial_status);

    return initialStatusPosition;
});

const initialAvailableInitialStatus = computed(() => {
    if (!model.value.ltd_final_status) return 1000;

    const {position: finalStatusPosition} = availableStatuses.find(({id}) => id === +model.value.ltd_final_status);

    return finalStatusPosition;
});

function updateModelFromQuery(query) {
    if (!query.options) return;

    for (const key in model.value) {
        if (query.options[key]) {
            model.value[key] = query.options[key];
        } else {
            model.value[key] = '';
        }
    }
}

function handleReset() {
    model.value = {...INITIAL_MODEL};
}

onMounted(() => {
    window.initVueFilters();
});

watch(() => model.value.ltd_initial_status, (newStatus) => {
    if (!newStatus) model.value.ltd_final_status = '';
});
</script>

<template>
    <ChartFilters
        :chart-type="CHART_TYPE.LTD"
        @query-change="updateModelFromQuery"
        @query-reset="handleReset"
    >
        <template #custom-filters>
            <div class="chart-filter__statuses">
                <div class="chart-filter__select select">
                    <label for="ltd_initial_status">
                        {{ t('filters.label_for_start_status_filter') }}
                    </label>
                    <select
                        id="ltd_initial_status"
                        v-model="model.ltd_initial_status"
                        name="ltd_initial_status"
                    >
                        <option value="">
                            &nbsp;
                        </option>
                        <option
                            v-for="status in availableStatuses"
                            :key="status.id"
                            :value="status.id"
                            :disabled="initialAvailableInitialStatus <= status.position"
                        >
                            {{ status.name }}
                        </option>
                    </select>
                </div>
                <div class="chart-filter__select select">
                    <label for="ltd_final_status">
                        {{ t('filters.label_for_end_status_filter') }}
                    </label>
                    <select
                        id="ltd_final_status"
                        v-model="model.ltd_final_status"
                        name="ltd_final_status"
                        :disabled="isFinalStatusBlocked"
                    >
                        <option value="">
                            &nbsp;
                        </option>
                        <option
                            v-for="status in availableStatuses"
                            :key="status.id"
                            :value="status.id"
                            :disabled="initialAvailableFinalStatus >= status.position"
                        >
                            {{ status.name }}
                        </option>
                    </select>
                </div>
                <label
                    for="only_business_days"
                    class="chart-filter__block checkbox"
                >
                    <input
                        id="only_business_days"
                        type="checkbox"
                        name="only_business_days"
                        :checked="!!model.only_business_days"
                    >
                    {{ t('filters.label_ignore_saturday_sunday') }}
                </label>
                <label
                    for="only_included_tasks"
                    class="chart-filter__block checkbox"
                >
                    <input
                        id="only_included_tasks"
                        type="checkbox"
                        name="only_included_tasks"
                        :checked="!!model.only_included_tasks"
                    >
                    {{ t('filters.label_only_included_tasks') }}
                    <a 
                        class="tooltip-icon"
                        :title="t('filters.label_question_icon_only_included_tasks')"
                    />
                </label>
            </div>
        </template>
    </ChartFilters>
</template>

<style lang="scss" scoped>
.chart-filter {
    &__label {
        white-space: nowrap;
    }

    &__statuses {
        display: flex;
        column-gap: 20px;
    }

    &__select {
        display: flex;
        align-items: center;
        column-gap: 4px;
        font-size: 12px;

        &:not(:first-child) {
            margin-top: 4px;
        }
    }

    &__block {
        display: flex;
        align-items: center;
        font-size: 12px;
        &.table-header {
            column-gap: 8px;
        }
        &.checkbox {
            column-gap: 4px;
        }
    }
}

.tooltip-icon {
    position: relative;
    min-width: 16px;
    height: 16px;
    background-image: url(../../assets/images/icons/help.png);;
    background-repeat: no-repeat;
    background-size: contain;
    cursor: pointer;
}
</style>
